import { createTheme } from "@mui/material/styles";
import { headlineFont, bodyFont } from "./fonts";
import { COLORS } from "./colors";

// Define common typography and components configurations
const commonTypography = {
  fontFamily: bodyFont, // Default font for the body text
  allVariants: {
    fontFamily: bodyFont, // Ensure all variants inherit body font by default
  },
  h1: {
    fontFamily: headlineFont,
    fontSize: "64px",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h2: {
    fontFamily: headlineFont,
    fontSize: "48px",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontFamily: headlineFont,
    fontSize: "36px",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  h4: {
    fontFamily: headlineFont,
    fontSize: "27px",
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h5: {
    fontFamily: headlineFont,
    fontSize: "20px",
    fontWeight: 500,
    lineHeight: 1.4,
  },
  h6: {
    fontFamily: headlineFont,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body1: {
    fontFamily: bodyFont,
    fontSize: "16px",
    lineHeight: 1.6,
  },
  body2: {
    fontFamily: bodyFont,
    fontSize: "14px",
    lineHeight: 1.5,
  },
};

const commonComponents: any = {
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }: { ownerState: any; theme: any }) => {
        const paletteColor =
          ownerState.color === "inherit"
            ? undefined
            : theme.palette[ownerState.color || "primary"];
        const visitedColor =
          ownerState.variant === "contained"
            ? paletteColor?.contrastText || theme.palette.common.white
            : paletteColor?.main || "inherit";

        return {
          // Link-backed buttons are anchors. Keep the button variant's label color
          // across interaction states instead of inheriting global anchor colors.
          "&:visited, &:hover, &:active": {
            color: visitedColor,
          },
        };
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        padding: theme.spacing(3),
        backgroundColor: "rgba(0,0,0,0.9)",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        backgroundColor: "rgba(0,0,0,0.9)",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiInputBase-root": {
          "& input": {
            color: "inherit",
          },
        },
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backdropFilter: "blur(10px)",
        backgroundColor: "rgba(10, 12, 16, 0.85)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        color: "#f5f7fa",
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: (theme: any) => ({
      a: {
        color: theme.palette.primary.main,
        textDecorationColor: "currentColor",
      },
      "a:visited": {
        color: COLORS.PRIMARY_VISITED,
      },
      "a:hover": {
        color: theme.palette.primary.light,
      },
      "a:active": {
        color: theme.palette.primary.main,
      },
    }),
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: "primary.main",
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
        "&:visited": {
          color: COLORS.PRIMARY_VISITED,
        },
        "&:active": {
          color: "primary.light",
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }: { theme: any }) => ({
        width: "100%",
        maxWidth: "100vw",
        padding: theme.spacing(2.5, 3),
        backgroundColor: "#2f3338",
        color: "#e9edf2",
        boxSizing: "border-box",
        overflowX: "hidden",
        [theme.breakpoints.up("sm")]: {
          width: 320,
        },
        [theme.breakpoints.up("md")]: {
          width: 280,
        },
        "& .MuiDivider-root": {
          borderColor: "rgba(255,255,255,0.08)",
        },
        "& .MuiTypography-h6": {
          fontSize: "18px",
          fontWeight: 700,
        },
        "& .MuiIconButton-root": {
          color: "#e9edf2",
        },
        "& .MobileNavDrawer__section": {
          borderRadius: theme.spacing(2),
          marginBottom: theme.spacing(1),
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
        "& .MobileNavDrawer__section .MuiListItemText-primary": {
          fontSize: "1rem",
          fontWeight: 600,
          color: "#e9edf2",
        },
        "& .MobileNavDrawer__section--primary .MuiListItemText-primary": {
          color: theme.palette.primary.main,
        },
        "& .MobileNavDrawer__link": {
          textDecoration: "none",
          borderRadius: theme.spacing(2),
          marginBottom: theme.spacing(1),
          paddingLeft: theme.spacing(4),
        },
        "& .MobileNavDrawer__link:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "& .MobileNavDrawer__link .MuiListItemText-primary": {
          fontSize: "0.9rem",
          fontWeight: 500,
          color: "#e9edf2",
        },
        "& .MobileNavDrawer__topLink": {
          textDecoration: "none",
          borderRadius: theme.spacing(2),
          marginBottom: theme.spacing(1),
        },
        "& .MobileNavDrawer__topLink:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "& .MobileNavDrawer__topLink .MuiListItemText-primary": {
          fontSize: "1rem",
          fontWeight: 500,
          color: "#e9edf2",
        },
        "& .MobileNavDrawer__icon": {
          minWidth: 32,
          color: "#e9edf2",
        },
        "& .MobileNavDrawer__cta": {
          textDecoration: "none",
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          cursor: "pointer",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        },
        "& .MobileNavDrawer__cta:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MobileNavDrawer__cta .MuiListItemText-primary": {
          fontSize: "1rem",
          fontWeight: 600,
          color: theme.palette.common.white,
        },
      }),
    },
  },
};

// Create a single theme configuration
export const theme = createTheme({
  typography: commonTypography,
  palette: {
    mode: "dark",
    primary: {
      main: COLORS.PRIMARY,
      light: COLORS.PRIMARY_LIGHT,
      contrastText: "#ffffff",
    },
    secondary: {
      main: COLORS.SECONDARY,
      contrastText: "#ffffff",
    },
    background: {
      default: "#020202ff",
      paper: "#151a20",
    },
    text: {
      primary: "#f5f7fa",
      secondary: "#c7cbd1",
    },
    divider: "rgba(255, 255, 255, 0.12)",
  },
  components: commonComponents,
});
